/**
 * useCharacterLibrary Hook - Local-First with IndexedDB
 * Stores characters in local IndexedDB for full quality persistence
 */

import { useState, useEffect, useCallback } from 'react';
import { Character, CharacterImage } from '../types';
import { LocalCharacter, LocalCharacterImage } from '../types/project';
import {
    saveCharacter as saveToIndexedDB,
    getAllCharacters,
    deleteCharacter as deleteFromIndexedDB,
    blobToBase64,
    base64ToBlob,
    saveFolder,
    getFoldersByType,
    deleteFolder as deleteFolderFromDB
} from '../services/localMediaDB';
import useLocalStorage from './useLocalStorage';
import { LocalFolder } from '../types/project';

export function useCharacterLibrary() {
    const [characters, setCharacters] = useState<Character[]>([]);
    const [folders, setFolders] = useState<LocalFolder[]>([]); // v3
    const [isLoading, setIsLoading] = useState(true);

    // Legacy storage for migration
    const [legacyCharacters, setLegacyCharacters] = useLocalStorage<Character[]>('character-library', []);

    // Load characters from IndexedDB on mount
    useEffect(() => {
        loadCharacters();
    }, []);

    const loadCharacters = async () => {
        setIsLoading(true);
        try {
            // 1. Load from IndexedDB
            const localCharacters = await getAllCharacters();
            const localFolders = await getFoldersByType('character'); // v3

            // 2. Convert to UI format (Blob -> Base64)
            const formattedCharacters: Character[] = await Promise.all(
                localCharacters.map(async (lc) => {
                    const images: CharacterImage[] = await Promise.all(
                        lc.images.map(async (img) => ({
                            name: img.name,
                            type: img.type,
                            base64: await blobToBase64(img.blob),
                            label: img.label
                        }))
                    );

                    return {
                        id: lc.id,
                        name: lc.name,
                        description: lc.description,
                        images,
                        voiceName: lc.voiceName,
                        sampleText: lc.sampleText,
                        folderId: lc.folderId // v3
                    };
                })
            );

            // 3. Check for migration needed (if IndexedDB is empty but localStorage has data)
            if (formattedCharacters.length === 0 && legacyCharacters.length > 0) {
                console.log('[useCharacterLibrary] Migrating legacy characters to IndexedDB...');
                await migrateLegacyCharacters(legacyCharacters);
                // Reload after migration
                return;
            }

            setCharacters(formattedCharacters);
            setFolders(localFolders); // v3
            console.log(`[useCharacterLibrary] Loaded ${formattedCharacters.length} characters and ${localFolders.length} folders`);
        } catch (error) {
            console.error('[useCharacterLibrary] Failed to load characters:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const migrateLegacyCharacters = async (legacyChars: Character[]) => {
        for (const char of legacyChars) {
            await addCharacter(char);
        }
        // Optional: Clear legacy storage after successful migration?
        // For safety, we keep it for now or user can clear cache.
        console.log('[useCharacterLibrary] Migration complete');
    };

    const addCharacter = useCallback(async (newChar: Character) => {
        try {
            // Convert UI format (Base64) to DB format (Blob)
            const localImages: LocalCharacterImage[] = newChar.images.map(img => ({
                name: img.name,
                type: img.type,
                label: img.label,
                blob: base64ToBlob(img.base64, img.type)
            }));

            const localChar: LocalCharacter = {
                id: newChar.id,
                name: newChar.name,
                description: newChar.description,
                images: localImages,
                voiceName: newChar.voiceName,
                sampleText: newChar.sampleText,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                folderId: newChar.folderId // v3
            };

            await saveToIndexedDB(localChar);

            // Update State
            setCharacters(prev => {
                // Replace if exists, else add
                const exists = prev.some(c => c.id === newChar.id);
                if (exists) {
                    return prev.map(c => c.id === newChar.id ? newChar : c);
                }
                return [...prev, newChar];
            });

        } catch (error) {
            console.error('[useCharacterLibrary] Failed to save character:', error);
            throw error;
        }
    }, []);

    const deleteCharacter = useCallback(async (charId: string) => {
        try {
            await deleteFromIndexedDB(charId);
            setCharacters(prev => prev.filter(c => c.id !== charId));
        } catch (error) {
            console.error('[useCharacterLibrary] Failed to delete character:', error);
        }
    }, []);

    const updateCharacter = useCallback(async (updatedChar: Character) => {
        await addCharacter(updatedChar); // saveToIndexedDB relies on put() which updates if ID exists
    }, [addCharacter]);

    // === FOLDER OPERATIONS (v3) ===

    const createFolder = useCallback(async (name: string, parentId: string | null = null) => {
        const newFolder: LocalFolder = {
            id: crypto.randomUUID(),
            name,
            parentId,
            type: 'character',
            createdAt: new Date().toISOString()
        };
        await saveFolder(newFolder);
        setFolders(prev => [...prev, newFolder]);
        return newFolder.id;
    }, []);

    const deleteFolder = useCallback(async (folderId: string) => {
        await deleteFolderFromDB(folderId);
        // Move contents to root or handle elsewhere? For now, we update state.
        setFolders(prev => prev.filter(f => f.id !== folderId));

        // Optional: Move orphaned characters to root
        const orphanedChars = characters.filter(c => c.folderId === folderId);
        for (const char of orphanedChars) {
            await updateCharacter({ ...char, folderId: undefined });
        }
    }, [characters, updateCharacter]);

    return {
        characters,
        folders, // v3
        addCharacter,
        deleteCharacter,
        updateCharacter,
        createFolder, // v3
        deleteFolder, // v3
        isLoading
    };
}
